import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ConfirmDialog, FilterBar, ListPage, StatusBadge, useToast, type Column } from "@cx/ui";
import { formatDocument } from "@cx/domain";
import type { ContactListItem, ListResult } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { useSession } from "../../session";
import { errorMessage } from "../../errors";
import type { ContactsSearch } from "./search";

const STATUS = {
  active: ["success", "Ativo"],
  inactive: ["secondary", "Inativo"],
  no_movement: ["warning", "Sem movimento"],
} as const;

export function ContactsListPage(): ReactElement {
  const search = useSearch({ from: "/cadastros/contatos" });
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const [selected, setSelected] = useState<string[]>([]);
  const [confirm, setConfirm] = useState(false);
  const types = useQuery({
    queryKey: ["contact-types", companyId],
    queryFn: () => api<{ data: { id: string; name: string }[] }>("/contacts/types"),
  });
  const list = useInfiniteQuery({
    queryKey: ["contacts", companyId, search],
    initialPageParam: "",
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(search)) if (v) params.set(k, v);
      if (pageParam) params.set("cursor", pageParam);
      return api<ListResult<ContactListItem>>(`/contacts?${params}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const goList = (s: ContactsSearch) => nav({ to: "/cadastros/contatos", search: s });
  const setFilter = (patch: Partial<ContactsSearch>) => goList({ ...search, ...patch });
  const remove = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await api(`/contacts/${id}`, { method: "DELETE" });
    },
    onSuccess: (_d, ids) => {
      toast("success", ids.length === 1 ? "Contato excluído" : `${ids.length} contatos excluídos`);
      setSelected([]);
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
    // Mesmo com falha no meio do lote, parte foi excluída: lista e Consulta de exclusões precisam refletir (FE16).
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["contacts", companyId] });
      qc.invalidateQueries({ queryKey: ["deletions", companyId] });
    },
  });
  const exportFile = useMutation({
    mutationFn: (fileFormat: "csv" | "xlsx") =>
      api<{ jobId: string }>("/contacts/export", {
        method: "POST",
        body: JSON.stringify({ q: search.q, type: search.type, status: search.status, fileFormat }),
      }),
    onSuccess: () => {
      toast("info", "Exportação iniciada. Acompanhe em Ações assíncronas, no topo da página.");
      qc.invalidateQueries({ queryKey: ["jobs", companyId] });
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const columns: Column<ContactListItem>[] = [
    // Link do router: ctrl/cmd+clique abre em nova aba e não recarrega a SPA (FE21).
    {
      key: "name",
      header: "Nome",
      cell: (r) => (
        <Link to="/cadastros/contatos/$id" params={{ id: r.id }}>
          {r.name}
        </Link>
      ),
    },
    { key: "doc", header: "CPF/CNPJ", cell: (r) => (r.document ? formatDocument(r.document) : "—") },
    { key: "phone", header: "Telefone", cell: (r) => r.mobile ?? r.phone ?? "—" },
    { key: "city", header: "Cidade", cell: (r) => (r.city ? `${r.city}/${r.uf}` : "—") },
    { key: "types", header: "Tipos", cell: (r) => r.types.join(", ") || "—" },
    {
      key: "status",
      header: "Situação",
      cell: (r) => <StatusBadge tone={STATUS[r.status][0]}>{STATUS[r.status][1]}</StatusBadge>,
    },
  ];
  const activeFilters = [search.q, search.type, search.status].filter(Boolean).length;
  const canCreate = can(perms, "contacts", "create");
  return (
    <>
      <ListPage<ContactListItem>
        title="Clientes e Fornecedores"
        breadcrumb={["Cadastros", "Clientes e Fornecedores"]}
        primaryAction={
          <>
            {can(perms, "contacts", "export") && (
              <div className="btn-group" role="group" aria-label="Exportar">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={exportFile.isPending}
                  onClick={() => exportFile.mutate("csv")}
                >
                  Exportar CSV
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={exportFile.isPending}
                  onClick={() => exportFile.mutate("xlsx")}
                >
                  Exportar Excel
                </button>
              </div>
            )}
            {canCreate && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => nav({ to: "/preferencias/importacoes", search: { tipo: "contacts" } })}
              >
                Importar planilha
              </button>
            )}
            {canCreate && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => nav({ to: "/cadastros/contatos/novo" })}
              >
                Incluir contato
              </button>
            )}
          </>
        }
        filters={
          <FilterBar
            search={search.q ?? ""}
            onSearch={(q) => setFilter({ q: q || undefined })}
            activeCount={activeFilters}
            onClear={() => goList({})}
            placeholder="Nome, fantasia, CPF/CNPJ ou e-mail"
          >
            <select
              className="form-select w-auto"
              aria-label="Tipo de contato"
              value={search.type ?? ""}
              onChange={(e) => setFilter({ type: e.target.value || undefined })}
            >
              <option value="">Todos os tipos</option>
              <option value="none">Sem tipo</option>
              {types.data?.data.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <select
              className="form-select w-auto"
              aria-label="Situação"
              value={search.status ?? ""}
              onChange={(e) =>
                setFilter({ status: (e.target.value || undefined) as ContactsSearch["status"] })
              }
            >
              <option value="">Todas as situações</option>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
              <option value="no_movement">Sem movimento</option>
            </select>
          </FilterBar>
        }
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        selected={selected}
        onSelectedChange={setSelected}
        selectionScope={JSON.stringify(search)}
        bulkActions={
          can(perms, "contacts", "delete")
            ? [{ label: "Excluir selecionados", tone: "danger", onClick: () => setConfirm(true) }]
            : []
        }
        // O total vem só da 1ª página: nas páginas com cursor a Task 9 devolve summary.count = -1.
        info={{ count: list.data?.pages[0]?.summary.count ?? 0 }}
        empty={{
          title: activeFilters
            ? "Nenhum contato encontrado com esses filtros"
            : "Nenhum contato cadastrado ainda",
          description: activeFilters
            ? undefined
            : "Cadastre manualmente agora. A importação por planilha chega na Fase 1.",
          actions: activeFilters
            ? [{ label: "Limpar filtros", onClick: () => goList({}) }]
            : canCreate
              ? [{ label: "Incluir contato", onClick: () => nav({ to: "/cadastros/contatos/novo" }) }]
              : [],
        }}
        loading={list.isLoading || list.isFetchingNextPage}
        error={list.isError ? list.error.message : undefined}
        hasMore={list.hasNextPage}
        onLoadMore={() => list.fetchNextPage()}
        onRowOpen={(r) => nav({ to: "/cadastros/contatos/$id", params: { id: r.id } })}
      />
      <ConfirmDialog
        open={confirm}
        title="Excluir contatos"
        message={`Excluir ${selected.length} ${selected.length === 1 ? "contato" : "contatos"}? Você pode restaurar em Preferências › Consulta de exclusões.`}
        confirmLabel="Excluir"
        onConfirm={() => {
          setConfirm(false);
          remove.mutate(selected);
        }}
        onCancel={() => setConfirm(false)}
      />
    </>
  );
}
